import axios, { type InternalAxiosRequestConfig } from 'axios';

import { analytics } from './analytics.service';
import { ConfigService } from './config.service';

export interface HttpClientHooks {
  /** Bearer token attached to every request; `null` when logged out. */
  getToken: () => string | null;
  /** Called on any 401 so the app can clear its session and redirect to login. */
  onUnauthorized: () => void;
}

// Defaults keep the client usable before `configureHttpClient` runs (Storybook,
// isolated tests). The app bootstrap wires the real session handlers.
let hooks: HttpClientHooks = {
  getToken: () => null,
  onUnauthorized: () => {},
};

/** Wire session handling into the shared client. Call once at app bootstrap. */
export const configureHttpClient = (next: HttpClientHooks): void => {
  hooks = next;
};

const config = ConfigService.getInstance();
const appId = config.get('appId');

// Fails at import, as before, when the bundle was built without an API URL.
config.getOrThrow('apiUrl');

// No `baseURL` on the instances: `attachRequestHeaders` reads it on every
// request, so a dashboard on an organization's custom domain switches to that
// organization's API as soon as `runtime-endpoints` resolves it.
const http = axios.create();

/**
 * Same base URL and the same request interceptor as `http` (bearer token,
 * `x-timezone`, `x-app-id`), but **without** the 401 handler.
 *
 * The auth flows need that difference: a wrong password answers 401 and must
 * not run the shared client's "session expired" logout, and a logout revoke
 * runs after the local session is already cleared. They used to reach for plain
 * `axios` for this, which silently dropped `x-app-id` — and that header is how
 * the backend knows which dashboard a login came from, now that identity is
 * `(platform, email)`. Going through a configured instance means the next auth
 * call inherits the header instead of having to remember it.
 */
const authHttp = axios.create();

const ID_SEGMENT = /^([0-9a-f]{24}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+)$/i;

/**
 * `/agent-config/66a…/generate?x=1` → `/agent-config/:id/generate`: analytics
 * groups failures by endpoint, so ids and query strings must not fan out.
 */
export const normalizeEndpoint = (url: string | undefined, baseURL?: string): string => {
  if (!url) return 'unknown';
  let path = url;
  if (baseURL && path.startsWith(baseURL)) path = path.slice(baseURL.length);
  path = path.split('?')[0].split('#')[0];
  return path
    .split('/')
    .map((segment) => (ID_SEGMENT.test(segment) ? ':id' : segment))
    .join('/');
};

const attachRequestHeaders = (config: InternalAxiosRequestConfig) => {
  config.baseURL ??= ConfigService.getInstance().getOrThrow('apiUrl');
  const token = hooks.getToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  config.headers['x-timezone'] =
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  // Which dashboard is calling. The backend resolves the caller's platform
  // from it on unauthenticated requests (login, register, recovery), and
  // stamps it on its analytics facts.
  config.headers['x-app-id'] = appId;
  return config;
};

http.interceptors.request.use(attachRequestHeaders, (error) =>
  Promise.reject(error),
);

authHttp.interceptors.request.use(attachRequestHeaders, (error) =>
  Promise.reject(error),
);

http.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (axios.isAxiosError(error) && !axios.isCancel(error)) {
      analytics.capture('api:request_failed', {
        method: (error.config?.method ?? 'get').toUpperCase(),
        status: error.response?.status ?? 0,
        endpoint: normalizeEndpoint(error.config?.url, error.config?.baseURL),
        is_network_error: !error.response,
      });
    }
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      hooks.onUnauthorized();
    }
    return Promise.reject(error);
  },
);

export { authHttp };
export default http;
